
function roundTo(num, precision = 2) {
  const factor = Math.pow(10, precision);
  return Math.round(num * factor) / factor;
}

class Trail {
  version = 1; // Version of the trail format

  constructor() {
    this.tracks = [];
    this.startTime = Date.now();

    // Instance properties for tracking state
    this.currentState = undefined;
    this.lastState = undefined;
    this.debounceTimeout = null;
    this.lastMouseMove = 0;
    this.lastMoveCoords = { x: null, y: null };
    this.mouseMoveThrottle = 100; // milliseconds
    this.resizeDebounceTimeout = null;
    this.scrollDebounceTimeout = null;
    this.eventListenersInitialized = false;
  }

  get output() {
    return window.exam_session_answer_trail_attributes_meta;
  }

  get isTrackable() {
    return Boolean(this.output);
  }

  start() {
    this.tracks = [];
    this.startTime = Date.now();
    this.track({k: "start", v: "Session started"});
    this.track({ k: "window", v: `${window.innerWidth}x${window.innerHeight}` });
    this.initEventListeners();
    this.blackoutOff();
    this.handleActivityEvent();
  }

  destroy() {
    this.tracks = [];
    this.removeEventListeners();
    this.blackoutOff();
  }

  track({t, ...rest}) {
    const item = {t: Date.now() - this.startTime, ...rest};
    this.tracks.push(item);
    this.onTrack(item);
  }

  onTrack(track) {
    if(this.output) this.output.value = this.serialize();
    // console.log(track);
  }

  add(item) {
    this.tracks.push(item);
  }

  serialize() {
    return JSON.stringify({
      version: this.version,
      startTime: this.startTime,
      touchEnabled: "ontouchstart" in window || navigator.maxTouchPoints > 0,
      screen: `${window.screen.width}x${window.screen.height}`,
      userAgent: navigator.userAgent,
      tracks: this.tracks,
    });
  }

  initEventListeners() {
    if(this.eventListenersInitialized) return;

    // Initialize activity tracking - using this object as event handler
    document.addEventListener("visibilitychange", this);
    window.addEventListener("focus", this);
    window.addEventListener("blur", this);

    // User interaction events - using this object as event handler
    document.addEventListener("keydown", this);
    document.addEventListener("focusin", this);
    document.addEventListener("pointerdown", this);

    window.addEventListener("mousemove", this);
    window.addEventListener("resize", this);
    window.addEventListener("scroll", this);
    this.eventListenersInitialized = true;
  }

  removeEventListeners() {
    // Remove all event listeners
    document.removeEventListener("visibilitychange", this);
    window.removeEventListener("focus", this);
    window.removeEventListener("blur", this);

    document.removeEventListener("keydown", this);
    document.removeEventListener("focusin", this);
    document.removeEventListener("pointerdown", this);

    window.removeEventListener("mousemove", this);
    window.removeEventListener("resize", this);
    window.removeEventListener("scroll", this);
    this.eventListenersInitialized = false;
  }



  // Main event handler using handleEvent pattern
  handleEvent(event) {
    const { type } = event;

    switch (type) {
      case "visibilitychange":
      case "focus":
      case "blur":
        this.handleActivityEvent();
        break;

      case "mousemove":
        this.handleMouseMove(event);
        break;

      case "keydown":
        this.track({ k: type, v: this.humanizeKey(event) });
        break;

      case "focusin":
        this.track({ k: type, v: this.getXPath(event.target) });
        break;

      case "pointerdown":
        this.track({ k: type, v: this.getXPath(event.target), type: event.pointerType });
        break;

      case "resize":
        this.handleResize(event);
        break;

      case "scroll":
        this.handleScroll();
        break;

      case "turbo:load":
        this.start();
        break;
    }
  }

  handleMouseMove(event) {
    const now = Date.now();
    if (now - this.lastMouseMove < this.mouseMoveThrottle) return;
    this.lastMouseMove = now;

    // Convert to relative coordinates (0 to 1)
    const coords = {
      x: roundTo(event.clientX / window.innerWidth, 4),
      y: roundTo(event.clientY / window.innerHeight, 4)
    };

    if (this.lastMoveCoords.x === coords.x && this.lastMoveCoords.y === coords.y) return;
    this.lastMoveCoords = coords;

    this.track({ k: event.type, v: `${coords.x},${coords.y}` });
  }


  blackoutOn() {
    document.documentElement.classList.add("inactive");
  }

  blackoutOff(){
    document.documentElement.classList.remove("inactive");
  }

  handleActivityEvent() {
    clearTimeout(this.debounceTimeout);

    this.debounceTimeout = setTimeout(() => {
      this.lastState = this.currentState;
      this.currentState = document.visibilityState === "visible" && document.hasFocus();
      if (this.lastState === this.currentState) return;

      this.track({ k: "active", v: this.currentState });

      if(this.currentState) {
        this.blackoutOff();
      }else{
        this.blackoutOn();
      }
    }, 10);
  }

  handleScroll() {
    clearTimeout(this.scrollDebounceTimeout);
    this.scrollDebounceTimeout = setTimeout(() => {
      // Calculate max scroll dimensions
      const maxScrollX = Math.max(0, document.documentElement.scrollWidth - window.innerWidth);
      const maxScrollY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);

      // Convert to relative positions (0 to 1)
      const relativeX = maxScrollX > 0 ? roundTo(window.scrollX / maxScrollX, 4) : 0;
      const relativeY = maxScrollY > 0 ? roundTo(window.scrollY / maxScrollY, 4) : 0;

      this.track({ k: "scroll", v: `${relativeX},${relativeY}` });
    }, 100);
  }

  handleResize(event) {
    clearTimeout(this.resizeDebounceTimeout);
    this.resizeDebounceTimeout = setTimeout(() => {
      this.track({ k: event.type, v: `${window.innerWidth}x${window.innerHeight}` });
    }, 250);
  }

  humanizeKey(event) {
    const out = new Set();
    if (event.ctrlKey) out.add("Control");
    if (event.shiftKey) out.add("Shift");
    if (event.altKey) out.add("Alt");
    if (event.metaKey) out.add("Meta");
    out.add(event.key);

    return Array.from(out).join("+");
  }

  getXPath(element) {
    if (element.id) {
      return `//*[@id="${element.id}"]`;
    }
    if (element === document.body) {
      return "/html/body";
    }

    const index = Array.from(element.parentNode.children).indexOf(element) + 1;
    const tagName = element.tagName.toLowerCase();
    return `${this.getXPath(element.parentNode)}/${tagName}[${index}]`;
  }
}

const trail = new Trail();


document.addEventListener("turbo:load", () => {
  trail.isTrackable ? trail.start() : trail.destroy();
});
